/**
 * DRILL-THROUGH — every mark on every chart is a control.
 *
 * One hook, one shape, so a bar, a pie slice, a line dot and a heat cell all
 * behave identically: clicking applies that value as a filter, jumps to Records
 * pre-filtered to exactly those rows, and leaves a chip that removes it again.
 *
 * Before this the ENTIRE three-screen surface had exactly one drill-through —
 * the building-league row — and it re-scoped the page rather than reaching rows.
 *
 * ── THE DIMENSION DECIDES THE FIELD ──
 *
 * The mapping is here rather than at each call site, because a panel knows its
 * dimension and should not also have to know the filter's shape. A dimension
 * with no mapping produces no drill at all rather than a click that appears to
 * work and changes nothing.
 */
import { useCallback, useMemo } from 'react';
import {
  DashboardFilter,
  DrillSelection,
  Granularity,
} from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import {
  addDays,
  addMonths,
  isDayKey,
} from '../../../../../function-utils/building-time';

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Whatever recharts hands a click handler. Deliberately loose: the shape
 *  differs per mark type (a Bar gets the datum, a Pie gets the slice, an
 *  activeDot gets the point) and every one of them carries `payload`. */
export type MarkPayload = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface DrillHandlers {
  onMark: (payload: MarkPayload) => void;
  /** For a mark that already knows its own key/label. */
  onValue: (key: string, label: string) => void;
  enabled: boolean;
}

/** A bucket key -> the DAYS it covers, both inclusive.
 *
 *  A bucket key is already a calendar day on the clock the answer was cut on
 *  (the response's `tz`), so it is handled as a KEY and never turned into an
 *  instant here: the server resolves the keys on that same clock (R-T5). A week
 *  is seven days; a month is ITS OWN length — it was taken as thirty-one, which
 *  ran a 30-day month into the next one. */
export const bucketDays = (
  key: string,
  granularity: Granularity
): { from: string; to: string } | null => {
  if (!isDayKey(key)) return null;
  if (granularity === 'week') return { from: key, to: addDays(key, 6) };
  if (granularity === 'month') return { from: key, to: addDays(addMonths(key, 1), -1) };

  return { from: key, to: key };
};

/** `14` or `14:00` -> an inclusive hour window. */
const hourWindow = (key: string): { timeFrom: string; timeTo: string } | null => {
  const hour = Number(String(key).slice(0, 2));
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
  const hh = String(hour).padStart(2, '0');

  return { timeFrom: `${hh}:00`, timeTo: `${hh}:59` };
};

export const drillPatch = (
  dimension: string,
  key: string,
  filter: DashboardFilter
): Partial<DashboardFilter> | null => {
  switch (dimension) {
    case 'building': {
      const id = Number(key);

      return Number.isInteger(id) && id > 0 ? { buildingId: id, portfolioId: null } : null;
    }
    case 'portfolio': {
      const id = Number(key);

      return Number.isInteger(id) && id > 0 ? { portfolioId: id, buildingId: null } : null;
    }
    case 'kind':
      return key ? { kinds: [key] } : null;
    case 'severity':
      return key ? { severity: key } : null;
    case 'staff':
      // A staff group with no actor id is the "(not attributed to a person)"
      // bucket, which is not somebody a filter can name.
      return key ? { actors: [key] } : null;
    case 'day':
    case 'week': {
      const w = bucketDays(key, filter.granularity);

      return w ? { range: 'custom', from: w.from, to: w.to } : null;
    }
    case 'hour_of_day': {
      const w = hourWindow(key);

      return w ? w : null;
    }
    default:
      return null;
  }
};

export interface UseChartDrillOptions {
  panelId: string;
  dimension: string;
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
  /** The datum field that carries the group key. Defaults to `key`. */
  keyField?: string;
  labelField?: string;
}

export const useChartDrill = ({
  panelId,
  dimension,
  filter,
  dispatch,
  keyField = 'key',
  labelField = 'label',
}: UseChartDrillOptions): DrillHandlers => {
  const onValue = useCallback(
    (key: string, label: string) => {
      const patch = drillPatch(dimension, key, filter);
      if (!patch) return;
      const selection: DrillSelection = { source: panelId, dimension, key, label };
      dispatch({ type: 'drill', selection, patch });
    },
    [dimension, filter, dispatch, panelId]
  );

  const onMark = useCallback(
    (payload: MarkPayload) => {
      if (!payload) return;
      // recharts hands the datum directly on a Pie/Cell click and wraps it in
      // `payload` on a Bar or an activeDot. Both shapes reach here.
      const datum = payload.payload ?? payload;
      const key = datum?.[keyField];
      if (key == null || key === '') return;
      onValue(String(key), String(datum?.[labelField] ?? key));
    },
    [keyField, labelField, onValue]
  );

  const enabled = useMemo(
    () => drillPatch(dimension, 'probe', filter) !== null || dimension === 'building',
    [dimension, filter]
  );

  return { onMark, onValue, enabled };
};
